/**
 * Session storage and access-token expiry.
 *
 * The API issues a plain access token with a fixed lifetime and no refresh token, so a session ends
 * silently once that lifetime is up. Nothing tells the app when that happens, so it reads the `exp`
 * claim itself: without that, a dead token still looks like a login and the UI keeps offering
 * actions that can only fail with a 401.
 */

export const USER_TOKEN_KEY = 'bible_user_token';
export const USER_DATA_KEY = 'bible_user_data';

/** Fired whenever a session is dropped, so the UI can clear itself and prompt for a new login. */
export const AUTH_EXPIRED_EVENT = 'auth:expired';

/**
 * Treat a token as expired slightly before its real deadline, so a request that is about to be sent
 * does not die in flight against a token that lapses on the way to the server.
 */
const EXPIRY_MARGIN_MS = 5_000;

/**
 * @returns when the token expires, in epoch milliseconds, or null if it carries no readable `exp`.
 */
export function getTokenExpiry(token: string): number | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;

    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const exp = JSON.parse(json).exp;

    return typeof exp === 'number' ? exp * 1000 : null;
  } catch {
    return null;
  }
}

/**
 * A token with no readable expiry is reported as usable: the API is the authority on validity, and a
 * 401 clears the session anyway. Only a claim we can actually read is grounds for expiring it here.
 */
export function isTokenExpired(token: string | null): boolean {
  if (!token) return true;

  const expiresAt = getTokenExpiry(token);
  if (expiresAt === null) return false;

  return Date.now() >= expiresAt - EXPIRY_MARGIN_MS;
}

export function getStoredToken(): string | null {
  return localStorage.getItem(USER_TOKEN_KEY);
}

export function clearSession(): void {
  localStorage.removeItem(USER_TOKEN_KEY);
  localStorage.removeItem(USER_DATA_KEY);
}

/**
 * Drops the stored session and announces it. Safe to call repeatedly.
 */
export function expireSession(): void {
  clearSession();
  window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
}
